import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight, Music, Users, Disc3 } from "lucide-react";
import axios from "axios";
import { useAudio } from "../App";
import { useTranslation } from "react-i18next";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { t } = useTranslation();
  const [featuredTracks, setFeaturedTracks] = useState([]);
  const [stats, setStats] = useState({ tracks: 0, artists: 0 });
  const [loading, setLoading] = useState(true);
  const { playTrack } = useAudio();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tracksRes, statsRes] = await Promise.all([
          axios.get(`${API}/tracks?featured=true`),
          axios.get(`${API}/stats`)
        ]);
        setFeaturedTracks((tracksRes.data.tracks || tracksRes.data).slice(0, 4));
        setStats(statsRes.data.stats || statsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://www.Sapito-Records.com/wp-content/uploads/2025/03/cropped-banniere-sapito-1.jpg"
            alt="Sapito-Records Studio"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0908]/50 via-[#0A0908]/70 to-[#0A0908]" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="font-heading text-sm tracking-widest text-[#FFB347] mb-4 block">
              {t('home.label')}
            </span>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight flex items-center justify-center">
              <span>Sapito</span><span className="bg-gradient-to-r from-[#F5F5F0] to-[#FFB347] bg-clip-text text-transparent text-[0.5em] ml-1 mr-0.5 relative top-[0.1em]">-</span><span className="text-[#FFB347]">Records</span>
            </h1>
            <p className="text-xl md:text-2xl text-[#A8A29E] mb-8 max-w-2xl mx-auto">
              {t('home.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/catalogue" className="btn-primary inline-flex items-center gap-2" data-testid="hero-catalogue-btn">
                <Play className="w-5 h-5" />
                {t('home.listen')}
              </Link>
              <Link to="/artistes" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-artists-link">
                <Users className="w-5 h-5" />
                {t('artists.title')}
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-[#FFB347]/50 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-[#FFB347] rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <span className="font-heading text-4xl md:text-5xl text-[#FFB347]">100+</span>
              <p className="text-[#A8A29E] mt-2">{t('home.tracks')}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <span className="font-heading text-4xl md:text-5xl text-[#FFB347]">{stats.artists || 4}+</span>
              <p className="text-[#A8A29E] mt-2">{t('home.artists')}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <span className="font-heading text-4xl md:text-5xl text-[#FFB347]">2025</span>
              <p className="text-[#A8A29E] mt-2">{t('home.since')}</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <span className="font-heading text-4xl md:text-5xl text-[#FFB347]">FR</span>
              <p className="text-[#A8A29E] mt-2">Label Français</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Releases */}
      <section className="py-16 px-6 md:px-12" data-testid="featured-releases">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span className="font-heading text-sm tracking-widest text-[#FFB347] mb-2 block">
                SÉLECTION
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold">
                {t('home.featured')}
              </h2>
            </div>
            <Link 
              to="/catalogue" 
              className="hidden md:flex items-center gap-2 text-[#FFB347] hover:gap-3 transition-all"
              data-testid="view-all-tracks"
            >
              {t('home.viewAll')} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="card animate-pulse">
                  <div className="aspect-square bg-[#1a1a1a]" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-[#1a1a1a] rounded w-3/4" />
                    <div className="h-3 bg-[#1a1a1a] rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {(Array.isArray(featuredTracks) ? featuredTracks : []).map((track, index) => (
                <motion.div
                  key={track.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card group cursor-pointer"
                  onClick={() => playTrack(track, Array.isArray(featuredTracks) ? featuredTracks : [])}
                  data-testid={`featured-track-${index}`}
                >
                  <div className="aspect-square relative overflow-hidden">
                    <img
                      src={track.cover_image || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400"}
                      alt={track.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full bg-[#FFB347] flex items-center justify-center shadow-lg transform scale-75 group-hover:scale-100 transition-transform">
                        <Play className="w-8 h-8 text-[#0A0908] ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-display font-semibold text-lg truncate group-hover:text-[#FFB347] transition-colors">
                      {track.title}
                    </h3>
                    <p className="text-[#A8A29E] text-sm truncate">{track.artist}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-[#8B4513]">{track.genre}</span>
                      <span className="text-xs text-[#A8A29E]">{track.duration}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <Link 
            to="/catalogue" 
            className="md:hidden mt-8 btn-secondary inline-flex items-center gap-2 mx-auto"
          >
            Voir tout le catalogue <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0 vintage-glow" />
        <div className="max-w-7xl mx-auto relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-heading text-sm tracking-widest text-[#FFB347] mb-4 block">
                À PROPOS
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-6">
                Un Label Né de la Passion
              </h2>
              <p className="text-[#A8A29E] text-lg mb-6 leading-relaxed">
                Sapito-Records est un label indépendant français dédié à la production et la diffusion de musique électronique de qualité. Fondé par des passionnés, notre mission est de mettre en lumière des artistes talentueux et des sons authentiques.
              </p>
              <p className="text-[#A8A29E] mb-8 leading-relaxed">
                Avec plus de 100 productions à notre catalogue, nous couvrons un large spectre de genres : Deep House, Techno, Minimal, Melodic House et bien plus encore. Chaque track est sélectionnée avec soin pour garantir une expérience d'écoute exceptionnelle.
              </p>
              <div className="flex gap-4">
                <Link to="/artistes" className="btn-secondary inline-flex items-center gap-2" data-testid="about-artists-btn">
                  <Users className="w-5 h-5" />
                  Nos Artistes
                </Link>
                <Link to="/contact" className="text-[#FFB347] hover:underline inline-flex items-center gap-2" data-testid="about-contact-link">
                  Envoyer une démo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600"
                  alt="Studio"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating vinyl */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32">
                <div className="vinyl-record w-full h-full animate-spin-slow">
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-[#FFB347] rounded-full" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-[#141210]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Disc3 className="w-16 h-16 text-[#FFB347] mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Vous êtes Artiste ?
            </h2>
            <p className="text-[#A8A29E] text-lg mb-8 max-w-2xl mx-auto">
              Envoyez-nous votre démo ! Nous sommes toujours à la recherche de nouveaux talents pour enrichir notre catalogue.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="cta-demo-btn">
              Envoyer une Démo <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
